import { ImpactedCountryRegion } from '@api/api/catalyst-insights';
import { RegionIcon } from '@design/components/region-icon/RegionIcon';
import { FlagIcon } from '@terminal/components/flag-icon/FlagIcon';
import { RatingPill } from '@terminal/widgets/earnings-insight-drawer/earnings-insight-widget/components/rating-pill/RatingPill';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './ImpactedCountriesAndRegions.module.scss';

export interface ImpactedCountriesAndRegionsProps {
  impactedCountries: ImpactedCountryRegion[];
  impactedRegions: ImpactedCountryRegion[];
  title: string;
}

export const ImpactedCountriesAndRegions = ({
  impactedCountries,
  impactedRegions,
  title,
}: ImpactedCountriesAndRegionsProps) => {
  const { t } = useTranslation(['countries', 'insights']);

  const items = useMemo(
    () => [
      ...impactedCountries?.map(c => ({
        key: `country-${c.code}`,
        name: t(`countries:${c.code}`, {
          defaultValue: c.name,
        }),
        icon: (
          <FlagIcon countryCode={c.code} variant="circular" customSize={20} />
        ),
        rating: c.rating,
        description: c.description,
      })),
      ...impactedRegions?.map(r => ({
        key: `region-${r.code}`,
        name: r.name,
        icon: (
          <RegionIcon region={r.name} size={20} fillColor="var(--icon-soft)" />
        ),
        rating: r.rating,
        description: r.description,
      })),
    ],
    [impactedCountries, impactedRegions]
  );

  if (!items.length) {
    return null;
  }

  return (
    <div className={styles.root}>
      <div className={styles.title}>{title}</div>
      <div className={styles.table}>
        <div className={styles.headerRow}>
          <div className={styles.headerCell}>
            {t('insights:catalyst.regionsTable.countriesAndRegions')}
          </div>
          <div className={styles.headerCell}>
            {t('insights:catalyst.regionsTable.description')}
          </div>
          <div className={styles.headerCell}>
            {t('insights:catalyst.regionsTable.impact')}
          </div>
        </div>
        {items.map(item => {
          return (
            <div className={styles.row} key={item.key}>
              <div className={styles.countryCell}>
                <div className={styles.topRow}>
                  <div className={styles.iconWrapper}>{item.icon}</div>
                  <div className={styles.titleBlock}>
                    <span className={styles.name}>{item.name}</span>
                  </div>
                </div>
              </div>
              <div className={styles.description}>{item.description}</div>
              <div className={styles.impactCell}>
                <RatingPill rating={item.rating} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
